import React from "react";
import { Beer, Target, BarChart3, Sparkles, Check, MessageCircle, Mail, ArrowRight, Megaphone, Trophy, Crosshair } from "lucide-react";
import { KILTRO_BRAND } from "../lib/assets";

const PACKAGES = [
  {
    name: "Tap Take-Over",
    price: "$1.500.000",
    period: "/ campaña 2 semanas",
    tag: "Lanzamientos",
    color: "border-[#333]",
    features: [
      "Banner Home + sección Cervezas",
      "Hueso patrocinado en 10 bares aliados",
      "1 ruta temática dedicada (ej: Ruta Kross)",
      "Push notification a usuarios objetivo",
      "Reporte de impacto + reclamos",
    ],
  },
  {
    name: "Always-On",
    price: "$2.500.000",
    period: "/ trimestre",
    tag: "Awareness sostenido",
    highlight: true,
    color: "border-[#FEBA01]",
    features: [
      "Tu marca en hero rotativo de Home",
      "Hueso patrocinado en 30+ bares",
      "Sección educativa /cervezas con tu estilo",
      "Featured en feed de actividad",
      "Dashboard analítico mensual",
      "Account manager dedicado",
    ],
  },
  {
    name: "Festival Mode",
    price: "Custom",
    period: "según evento",
    tag: "Eventos / activaciones",
    color: "border-[#333]",
    features: [
      "Co-branding completo durante evento",
      "Mapa exclusivo del festival",
      "Huesos flash geolocalizados",
      "Integración con tu equipo de marketing",
      "Métricas en vivo durante el evento",
    ],
  },
];

export default function ForBrands() {
  return (
    <div className="bg-black text-[#F5F5F5]" data-testid="for-brands-page">
      {/* HERO */}
      <section className="relative overflow-hidden border-b-2 border-[#1A1A1A]">
        <div className="kiltro-stripes h-3" />
        <div className="max-w-6xl mx-auto px-4 lg:px-8 py-20">
          <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#FEBA01] flex items-center gap-2">
            <Beer size={12} /> Para marcas cerveceras
          </div>
          <h1 className="mt-3 font-display text-4xl sm:text-5xl lg:text-6xl uppercase tracking-tight leading-[1]">
            Llega al cervecero <br />
            <span className="inline-block bg-[#FEBA01] text-black px-3 py-1 -skew-x-[6deg]">en el momento exacto.</span>
          </h1>
          <p className="mt-6 max-w-2xl text-base lg:text-lg text-[#E5E5E5] leading-relaxed">
            Kiltro Scout es el único canal cervecero de Chile que conecta tu marca con el consumidor justo cuando
            está eligiendo dónde tomar su próxima chela. Tráfico medible. ROI real. Cero pauta a ciegas.
          </p>
          <div className="mt-8 flex flex-wrap gap-3">
            <a
              href={`mailto:${KILTRO_BRAND.contactEmail}?subject=${encodeURIComponent("Interés campaña marca cervecera")}`}
              className="btn-brutal inline-flex items-center gap-2 bg-[#FEBA01] text-black font-display text-lg uppercase tracking-wide px-6 py-3 shadow-brutal-white"
              data-testid="brands-email-cta"
            >
              <Mail size={18} /> Pedir media kit
            </a>
            <a
              href={`https://wa.me/${KILTRO_BRAND.whatsapp.replace(/\D/g, "")}?text=${encodeURIComponent("Hola Kiltro, represento una marca cervecera y me interesa una campaña.")}`}
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center gap-2 border-2 border-[#FEBA01] text-[#FEBA01] font-display text-lg uppercase tracking-wide px-6 py-3 hover:bg-[#FEBA01] hover:text-black transition-colors"
              data-testid="brands-whatsapp-cta"
            >
              <MessageCircle size={18} /> WhatsApp comercial
            </a>
          </div>

          <div className="mt-10 grid sm:grid-cols-3 gap-3 max-w-3xl">
            <KPI value="100%" label="Audiencia cervecera segmentada" />
            <KPI value="10x" label="ROI promedio campañas similares (Untappd)" />
            <KPI value="48h" label="Activación de campañas" />
          </div>
        </div>
      </section>

      {/* Por qué Kiltro */}
      <section className="max-w-6xl mx-auto px-4 lg:px-8 py-16">
        <div className="text-center max-w-2xl mx-auto mb-10">
          <div className="font-mono text-[10px] uppercase tracking-widest text-[#FEBA01]">La diferencia</div>
          <h2 className="mt-2 font-display text-3xl sm:text-4xl uppercase tracking-tight">¿Por qué Kiltro y no Instagram Ads?</h2>
        </div>
        <div className="grid md:grid-cols-3 gap-4">
          <Reason icon={Crosshair} title="Audiencia 100% cervecera" desc="No vendes cerveza a alguien con sed: la vendes a alguien que ya está eligiendo qué chela tomar HOY. Intención de compra máxima." />
          <Reason icon={BarChart3} title="Métricas reales, no impresiones" desc="No 'vio el ad'. Sabes cuántos reclamaron, en qué bar, qué cerveza pidieron y cuál fue el ticket promedio." />
          <Reason icon={Target} title="Geo + temporal" desc="Activa Huesos por barrio y franja horaria. Ideal para lanzar producto en Bellavista un viernes 22:00 y medir en vivo." />
        </div>
      </section>

      {/* Casos de uso */}
      <section className="bg-[#0F0F0F] border-y-2 border-[#1A1A1A]">
        <div className="max-w-6xl mx-auto px-4 lg:px-8 py-16">
          <div className="text-center max-w-2xl mx-auto mb-10">
            <div className="font-mono text-[10px] uppercase tracking-widest text-[#FEBA01]">Casos de uso</div>
            <h2 className="mt-2 font-display text-3xl sm:text-4xl uppercase tracking-tight">Cómo lo activan otras marcas</h2>
          </div>
          <div className="grid md:grid-cols-2 gap-4">
            <UseCase icon={Megaphone} title="Lanzamiento de nueva variedad" desc="Kross lanza una NEIPA edición limitada → Hueso '2x1 en NEIPA Kross' activo 2 semanas en 10 brewpubs aliados → 600 reclamos medidos + tendencia clara de aceptación." />
            <UseCase icon={Trophy} title="Sponsorship de ruta temática" desc="Tropera patrocina la 'Ruta Sour' en Lastarria → 4 bares aliados, cervezas Tropera destacadas, badge 'Sour Master' para usuarios que completen la ruta." />
            <UseCase icon={Sparkles} title="Reactivación off-peak" desc="Una marca regional necesita mover stock un martes lluvioso → Hueso flash '40% OFF' activo solo de 18-21h → 230 reclamos en 3 horas." />
            <UseCase icon={BarChart3} title="Insights de mercado" desc="Antes de lanzar formato lata, una marca compra reporte trimestral con tendencias de check-ins/ratings por barrio y demografía. Decisión data-driven en lugar de intuición." />
          </div>
        </div>
      </section>

      {/* Paquetes */}
      <section className="max-w-6xl mx-auto px-4 lg:px-8 py-16">
        <div className="text-center max-w-2xl mx-auto mb-10">
          <div className="font-mono text-[10px] uppercase tracking-widest text-[#FEBA01]">Paquetes comerciales</div>
          <h2 className="mt-2 font-display text-3xl sm:text-4xl uppercase tracking-tight">Diseñado para escalar</h2>
          <p className="mt-3 text-sm text-[#A3A3A3]">Trabajamos con marcas chicas y grandes. Ajustamos a tu objetivo, presupuesto y timing.</p>
        </div>
        <div className="grid md:grid-cols-3 gap-4">
          {PACKAGES.map((p) => (
            <div
              key={p.name}
              className={`bg-[#1A1A1A] border-4 ${p.color} p-6 flex flex-col ${p.highlight ? "shadow-brutal" : ""}`}
              data-testid={`brands-pkg-${p.name.toLowerCase().replace(/\s+/g, "-")}`}
            >
              {p.highlight && (
                <div className="bg-[#FEBA01] text-black font-mono text-[10px] uppercase tracking-[0.2em] inline-block px-2 py-1 -mt-9 mb-3 self-start">
                  Más elegido
                </div>
              )}
              <div className="font-mono text-[10px] uppercase tracking-widest text-[#A3A3A3] mb-1">{p.tag}</div>
              <div className="font-display text-2xl uppercase tracking-tight">{p.name}</div>
              <div className="mt-2 font-display">
                <span className="text-3xl text-[#FEBA01]">{p.price}</span>
                <span className="text-xs text-[#A3A3A3] ml-1 block">{p.period}</span>
              </div>
              <ul className="mt-5 space-y-2 flex-1">
                {p.features.map((f) => (
                  <li key={f} className="flex gap-2 text-sm text-[#E5E5E5]">
                    <Check size={16} className="text-[#FEBA01] shrink-0 mt-0.5" /> {f}
                  </li>
                ))}
              </ul>
              <a
                href={`mailto:${KILTRO_BRAND.contactEmail}?subject=${encodeURIComponent(`Interés paquete ${p.name}`)}`}
                className={`mt-6 btn-brutal text-center inline-flex items-center justify-center gap-2 ${
                  p.highlight ? "bg-[#FEBA01] text-black shadow-brutal-white" : "border-2 border-[#FEBA01] text-[#FEBA01] hover:bg-[#FEBA01] hover:text-black"
                } font-display uppercase px-4 py-3`}
                data-testid={`brands-pkg-cta-${p.name.toLowerCase().replace(/\s+/g, "-")}`}
              >
                Cotizar {p.name} <ArrowRight size={16} />
              </a>
            </div>
          ))}
        </div>
      </section>

      {/* CTA final */}
      <section className="max-w-6xl mx-auto px-4 lg:px-8 py-16">
        <div className="bg-black border-2 border-[#FEBA01] p-8 lg:p-12 text-center relative overflow-hidden">
          <h2 className="font-display text-3xl sm:text-5xl uppercase tracking-tight text-[#FEBA01] leading-[1]">
            Conversemos.
          </h2>
          <p className="mt-3 text-base text-[#E5E5E5] max-w-xl mx-auto">
            Planteamos un piloto a la medida y te damos acceso a métricas pre-campaña. Sin compromiso.
          </p>
          <div className="mt-6 flex flex-wrap gap-3 justify-center">
            <a
              href={`mailto:${KILTRO_BRAND.contactEmail}?subject=${encodeURIComponent("Quiero conversar con Kiltro Scout")}`}
              className="btn-brutal bg-[#FEBA01] text-black font-display text-lg uppercase px-6 py-3 inline-flex items-center gap-2 shadow-brutal-white"
              data-testid="brands-final-cta"
            >
              <Mail size={18} /> Escribir email
            </a>
          </div>
        </div>
      </section>
    </div>
  );
}

function KPI({ value, label }) {
  return (
    <div className="border-l-2 border-[#FEBA01] pl-3">
      <div className="font-display text-3xl text-[#FEBA01]">{value}</div>
      <div className="font-mono text-[10px] uppercase tracking-widest text-[#A3A3A3] leading-tight mt-1">{label}</div>
    </div>
  );
}

function Reason({ icon: Icon, title, desc }) {
  return (
    <div className="bg-[#1A1A1A] border-2 border-[#333] p-6 hover:border-[#FEBA01] transition">
      <div className="w-12 h-12 bg-[#FEBA01] text-black flex items-center justify-center"><Icon size={24} /></div>
      <h3 className="mt-4 font-display text-xl uppercase tracking-tight">{title}</h3>
      <p className="mt-2 text-sm text-[#A3A3A3] leading-relaxed">{desc}</p>
    </div>
  );
}

function UseCase({ icon: Icon, title, desc }) {
  return (
    <div className="bg-black border-2 border-[#FEBA01]/40 p-6 hover:border-[#FEBA01] transition">
      <Icon size={24} className="text-[#FEBA01] mb-3" />
      <h3 className="font-display text-lg uppercase tracking-tight">{title}</h3>
      <p className="mt-2 text-sm text-[#A3A3A3] leading-relaxed">{desc}</p>
    </div>
  );
}
